import axios from 'axios';
import { getLocalServerSnapshot, isLocalCatalogRoute } from '../context/LocalServerContext';
import { authStorage } from '../utils/authStorage';
import { setLicenseWarning, setLicenseLocked } from './license-status';

const hostname = window.location.hostname;
const isLocalDev =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.localhost');

const envApiUrl  = import.meta.env.VITE_API_URL as string | undefined;
const localMode  = import.meta.env.VITE_LOCAL_MODE === 'true';

// Handle IP addresses (Cloud testing/direct access)
const isIP = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname);
// For production instances with subdomains (e.g., admin.koolfood.io → koolfood.io)
const domainParts = hostname.split('.');
export const BASE_DOMAIN = domainParts.length >= 2 && !isIP ? domainParts.slice(-2).join('.') : null;
const baseDomain = BASE_DOMAIN;

const getApiUrl = () => {
    // Local mode: dashboard is served by the local server itself → same origin
    if (localMode) return `${window.location.protocol}//${window.location.host}/api/v1`;

    if (envApiUrl) return envApiUrl;
    if (isLocalDev) return 'http://localhost:5000/api/v1';
    if (isIP) return `${window.location.protocol}//${hostname}:5000/api/v1`;
    if (baseDomain) return `${window.location.protocol}//api.${baseDomain}/api/v1`;

    return `${window.location.protocol}//${hostname}:5000/api/v1`;
};

export const API_URL = getApiUrl();

// Derive Socket URL from API URL
const envSocketUrl = import.meta.env.VITE_SOCKET_URL as string | undefined;
export const SOCKET_URL = envSocketUrl || API_URL.replace(/\/api\/v1$/, '').replace(/^http/, 'ws');

// Derive Display Screens URL the same way — avoids hardcoding a brand's domain in .env.production
const envDisplayUrl = import.meta.env.VITE_DISPLAY_URL as string | undefined;
export const DISPLAY_URL = envDisplayUrl || (baseDomain ? `${window.location.protocol}//display.${baseDomain}` : 'http://localhost:3003');

console.log('App Config:', { API_URL, SOCKET_URL, DISPLAY_URL, isLocalDev, hostname });


const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add a request interceptor to add the token to all requests
api.interceptors.request.use(
    (config) => {
        const token = authStorage.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        const posId = sessionStorage.getItem('current_pos_id');
        if (posId && posId !== 'undefined') {
            config.headers['X-POS-ID'] = posId;
        }

        const restaurantId = localStorage.getItem('pos_restaurant_id');
        if (restaurantId && restaurantId !== 'undefined') {
            config.headers['X-Restaurant-ID'] = restaurantId;
        }

        const domainParts = window.location.hostname.split('.');
        if (domainParts.length >= 2 && !window.location.hostname.match(/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/)) {
            config.headers['X-Restaurant-Slug'] = domainParts[0];
        }

        // In local mode baseURL is already same-origin (the local-server serving
        // this very page) — never let the LocalServerContext probe override it
        // with a *different* local-server it happens to detect reachable (e.g. a
        // real Docker instance on the same dev machine's default port), which
        // would silently redirect requests to the wrong restaurant's data.
        const localServerUrl = getLocalServerSnapshot();
        if (!localMode && localServerUrl && config.url && isLocalCatalogRoute(config.url)) {
            config.baseURL = `${localServerUrl}/api/v1`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add a response interceptor to handle errors and extract messages
api.interceptors.response.use(
    (response) => {
        // Emitted by both the cloud gate and the local server's licenseGate.
        // Absent header → clears any stale warning (e.g. right after a renewal).
        setLicenseWarning(response.headers?.['x-license-warning']);
        return response;
    },
    (error) => {
        if (error.response && error.response.data && error.response.data.message) {
            // Use the message from the backend if available
            error.message = error.response.data.message;
        }
        // 423 = licence expired past the grace window. Surfaced as a single
        // blocking screen instead of one generic toast per failed action.
        if (error.response?.status === 423 && error.response?.data?.code === 'license_expired') {
            setLicenseLocked(error.response.data.message);
        } else {
            setLicenseWarning(error.response?.headers?.['x-license-warning']);
        }
        return Promise.reject(error);
    }
);

export const downloadFile = async (url: string, filename: string, mimeType = 'application/octet-stream') => {
    const response = await api.get(url, {
        responseType: 'blob'
    });
    const blob = new Blob([response.data], { type: mimeType });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
};

export { api };
export default api;
